import React, { useEffect, useState } from 'react';
import { supabase } from '../api/supabaseClient';
import { useTranslation } from 'react-i18next';
import {
    Container, Box, Typography, Grid, Card, CardMedia, CardContent,
    CircularProgress, IconButton, Dialog, DialogContent, DialogTitle, useTheme,
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

// --- Sabit Footer Bileşeni ---
const Footer = () => (
    <Box
        component="footer"
        sx={{
            position: 'fixed',
            left: 0,
            bottom: 0,
            width: '100vw',
            bgcolor: 'primary.dark',
            color: '#fff',
            py: 2,
            textAlign: 'center',
            zIndex: 999,
            boxShadow: '0 -2px 10px rgba(0,0,0,0.08)'
        }}
    >
        <Typography variant="body1" sx={{ fontWeight: 600, letterSpacing: 1 }}>
            Seyhan Erdem
        </Typography>
    </Box>
);

// --- Youtube ID Helper ---
const getYouTubeID = (url) => {
    const regExp = /^.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[1].length === 11) ? match[1] : null;
};

const Home = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openVideo, setOpenVideo] = useState(null);

    useEffect(() => {
        const fetchVideos = async () => {
            try {
                setLoading(true);
                const { data, error } = await supabase
                    .from('videos')
                    .select('id,title_en,title_tr,url')
                    .order('inserted_at', { ascending: false });
                if (error) throw error;
                setVideos(data || []);
            } catch (err) {
                console.error('Error fetching videos:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchVideos();
    }, []);

    const getVideoTitle = (video) => {
        if (i18n.language === 'tr') return video.title_tr || video.title_en || '';
        return video.title_en || video.title_tr || '';
    };

    return (
        <>
            <Container maxWidth="lg" sx={{ py: 4, pb: 10 /* Footer için alt boşluk */ }}>
                <Box sx={{ mt: 6, mb: 4, textAlign: 'center' }}>
                    {/* İstersen buraya başlık ekleyebilirsin */}
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <Grid container spacing={4}>
                            {videos.map((video) => {
                                const vid = getYouTubeID(video.url);
                                if (!vid) return null;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={video.id}>
                                        <Card
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderRadius: 3,
                                                boxShadow: 3,
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s',
                                                '&:hover': {
                                                    boxShadow: 10,
                                                    transform: 'translateY(-6px) scale(1.025)',
                                                },
                                                position: 'relative',
                                                background: 'transparent', // kart tamamen transparan
                                            }}
                                            onClick={() => setOpenVideo(video)}
                                        >
                                            <Box sx={{ position: 'relative' }}>
                                                <CardMedia
                                                    component="img"
                                                    image={`https://img.youtube.com/vi/${vid}/hqdefault.jpg`}
                                                    alt={getVideoTitle(video)}
                                                    sx={{
                                                        height: 210,
                                                        filter: 'brightness(0.8)',
                                                        transition: 'filter 0.3s',
                                                    }}
                                                />
                                                <IconButton
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        color: '#fff',
                                                        bgcolor: 'rgba(0,0,0,0.45)',
                                                        fontSize: 64,
                                                        p: 0.5,
                                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                                                    }}
                                                    aria-label="Oynat"
                                                >
                                                    <PlayCircleOutlineIcon sx={{ fontSize: 64 }} />
                                                </IconButton>
                                            </Box>
                                            <CardContent
                                                sx={{
                                                    flexGrow: 1,
                                                    minHeight: 64,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    textAlign: 'center',
                                                    background: 'transparent', // tam transparan!
                                                    boxShadow: 'none',
                                                    p: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: theme.palette.mode === 'dark' ? '#f9f9f9' : '#1a1a1a',
                                                        fontWeight: 500,
                                                        width: '100%',
                                                        textAlign: 'center',
                                                        background: 'transparent',
                                                    }}
                                                >
                                                    {getVideoTitle(video)}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
                {/* Video Modal */}
                <Dialog open={Boolean(openVideo)} onClose={() => setOpenVideo(null)} maxWidth="md" fullWidth>
                    {openVideo && (
                        <>
                            <DialogTitle>{getVideoTitle(openVideo)}</DialogTitle>
                            <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', bgcolor: '#000' }}>
                                <Box sx={{ width: '100%', aspectRatio: '16/9', position: 'relative' }}>
                                    <iframe
                                        width="100%"
                                        height="400"
                                        style={{ border: 0, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                        src={`https://www.youtube.com/embed/${getYouTubeID(openVideo.url)}?autoplay=1`}
                                        title={getVideoTitle(openVideo)}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </Box>
                            </DialogContent>
                        </>
                    )}
                </Dialog>
            </Container>
            {/*<Footer />*/}
        </>
    );
};

export default Home;
